import { ReactElement, useEffect, useState } from 'react';
import {
  useMediaQuery,
  MenuItem,
  Button,
  Pagination,
  styled,
} from '@mui/material';
import {
  StyledSelect,
  StyledTablePagination,
  useSort,
  variables,
} from '../common';
import {
  useCsaRequestSearch,
  CsaRequestStatus2d,
  CsaRequestStatusStack,
  CsaRequestStatusFilterSortStack,
  csaProperties,
  ICsaSearchableProperty,
  byLabel,
  labelP1,
  labelP2,
  enableP2,
  renderP1,
  renderP2,
} from '.';
import { pathOr, always, path } from 'rambda';
import { map, filter, sortBy as fSortBy } from 'fp-ts/Array';
import { flow } from 'fp-ts/function';

const rowsPerPageOptions = [10, 25, 50, 100, 500];

export const CsaRequestStatus = () => {
  const [searchBy, setSearchBy] = useState('reqType');
  const [searchP1, setSearchP1] = useState('');
  const [searchP2, setSearchP2] = useState('');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangePage2 = (_: unknown, newPage: number) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = flow(
    pathOr('10', 'target.value'),
    (a: string) => parseInt(a, 10),
    setPageSize,
    always(0),
    setPage
  );

  const { sortBy, sortDirection, toggleSort } = useSort('STATUS', 'asc');

  const { requestStatus, call, search } = useCsaRequestSearch({
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchBy,
    searchP1,
    searchP2,
  });

  useEffect(search, [sortBy, sortDirection, page, pageSize, search]);

  const matchesGridBreakpoint = useMediaQuery('(min-width:60em)', {
    noSsr: true,
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const toggleDrawer = (open: boolean) => (event: any) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }

    setOpenDrawer(open);
  };

  return (
    <Article>
      {!matchesGridBreakpoint && (
        <article style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Pagination
            count={Math.ceil(requestStatus.totalElements / pageSize)}
            page={page + 1}
            onChange={handleChangePage2}
            size="small"
          />
          <Button onClick={toggleDrawer(true)} variant="contained">
            search / sort
          </Button>
        </article>
      )}
      {matchesGridBreakpoint && (
        <Search onSubmit={flow(e => e.preventDefault(), search)}>
          <Pair>
            <label id="search-by">Search By:</label>
            <StyledSelect
              labelId="search-by"
              value={searchBy}
              onChange={flow(
                pathOr('', 'target.value'),
                setSearchBy,
                always(''),
                setSearchP1,
                always(''),
                setSearchP2
              )}
              variant="outlined">
              {flow(
                filter<ICsaSearchableProperty>(pathOr(false, 'searchable')),
                fSortBy([byLabel]),
                map<ICsaSearchableProperty, ReactElement>(
                  ({ value, label }) => (
                    <MenuItem value={value}>{label}</MenuItem>
                  )
                )
              )(csaProperties)}
            </StyledSelect>
          </Pair>
          <Pair>
            {path<string>(searchBy)(labelP1) !== void 0 && (
              <label id="search-parameter-1">
                {path<string>(searchBy)(labelP1)}
              </label>
            )}

            {renderP1({
              di: false,
              csa: true,
              searchBy,
              searchP1,
              setSearchP1,
            })}
          </Pair>

          {enableP2(searchBy) && (
            <Pair>
              <label id="search-parameter-2">
                {path<string>(searchBy)(labelP2)}
              </label>
              {renderP2({
                searchP2,
                setSearchP2,
              })}
            </Pair>
          )}

          <Button
            type="submit"
            variant="contained"
            style={{ marginLeft: '1em' }}>
            search
          </Button>
          <label id="search-parameter-help">
            For wildcard match, please type %25 after your input.
          </label>
        </Search>
      )}
      {matchesGridBreakpoint && requestStatus.totalElements > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'right' }}>
          <StyledTablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            count={requestStatus.totalElements}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      )}
      {matchesGridBreakpoint ? (
        <CsaRequestStatus2d
          properties={csaProperties}
          requests={requestStatus.content}
          getRequstsCallStatus={call}
          sortBy={sortBy}
          sortDirection={sortDirection}
          toggleSort={toggleSort}
        />
      ) : (
        <CsaRequestStatusStack
          properties={csaProperties}
          requests={requestStatus.content}
          getRequstsCallStatus={call}
        />
      )}
      {requestStatus.totalElements > pageSize && (
        <div style={{ display: 'flex', justifyContent: 'right' }}>
          <StyledTablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            count={requestStatus.totalElements}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      )}
      <CsaRequestStatusFilterSortStack
        properties={csaProperties}
        sortBy={sortBy}
        sortDirection={sortDirection}
        openDrawer={openDrawer}
        searchBy={searchBy}
        searchP1={searchP1}
        searchP2={searchP2}
        toggleSort={toggleSort}
        toggleDrawer={toggleDrawer}
        search={search}
        setSearchBy={setSearchBy}
        setSearchP1={setSearchP1}
        setSearchP2={setSearchP2}
      />
    </Article>
  );
};

const Article = styled('article')(({ theme }) => ({
  ...variables.childrenMarginTop2x,
}));

const Search = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',

  '&>*+*': {
    marginTop: '1em',
  },

  '@media (min-width: 60em)': {
    alignItems: 'center',
    flexDirection: 'row',

    '&>*+*': {
      marginLeft: '10rem',
      marginTop: '0em',
    },
  },
}));

const Pair = styled('section')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '&>*+*': {
    marginLeft: '1rem',
  },
}));
